/**
 * Nalorio storefront behavior.
 * - Pet name: typed once (hero), remembered in this browser, painted into [data-nl-pet] text
 *   and pre-filled into the "Pet name" personalization field on product pages.
 * - <nl-name-tag>: live engraving + swing on the hero tag.
 * - <nl-bed-finder>: weight slider -> bed size.
 * - Photo fields: size check + preview.
 * - Custom analytics events via Shopify.analytics.publish, picked up by the Nalorio custom pixel.
 */

const STORAGE_KEY = 'nalorio.petName';
const MAX_NAME = 12;
const MAX_PHOTO_MB = 20;

const petStore = {
  get() {
    try {
      return localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  },
  set(value) {
    try {
      if (value) localStorage.setItem(STORAGE_KEY, value);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage blocked: the name still works for this page view */
    }
  },
};

/** Letters, numbers, spaces, apostrophes, hyphens and periods; collapsed spaces; tag length. */
export function cleanName(value) {
  return value
    .replace(/[^\p{L}\p{N} .'\-]/gu, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s+/, '')
    .slice(0, MAX_NAME);
}

function publish(name, data = {}) {
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch {
    /* analytics must never break the page */
  }
}

const once = new Set();
function publishOnce(name, data) {
  if (once.has(name)) return;
  once.add(name);
  publish(name, data);
}

function paintNames(name) {
  const trimmed = name.trim();
  for (const el of document.querySelectorAll('[data-nl-pet]')) {
    const fallback = el.dataset.nlFallback || 'your pet';
    const base = trimmed || fallback;
    el.textContent = el.hasAttribute('data-nl-possessive') ? `${base}’s` : base;
  }
}

function prefillPropertyFields(name) {
  if (!name) return;
  for (const input of document.querySelectorAll('input[name="properties[Pet name]"]')) {
    if (input instanceof HTMLInputElement && !input.value) {
      input.value = name.trim().slice(0, input.maxLength > 0 ? input.maxLength : MAX_NAME);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}

function setPetName(name, source) {
  petStore.set(name.trim());
  paintNames(name);
  document.dispatchEvent(new CustomEvent('nalorio:pet-name', { detail: { name, source } }));
}

/* ---------- Hero name tag ---------- */

class NameTag extends HTMLElement {
  #controller = new AbortController();
  #publishTimer = 0;

  connectedCallback() {
    this.input = this.querySelector('[data-nl-tag-input]');
    this.texts = [...this.querySelectorAll('[data-nl-tag-text]')];
    this.swinger = this.querySelector('[data-nl-tag-swing]');
    this.placeholder = this.dataset.placeholder || 'Biscuit';
    if (!(this.input instanceof HTMLInputElement)) return;

    const saved = petStore.get();
    if (saved) this.input.value = saved;
    this.#render(this.input.value);

    const { signal } = this.#controller;
    this.input.addEventListener('input', () => this.#onInput(), { signal });
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.querySelector('[data-nl-tag-primary]')?.focus();
      }
    }, { signal });
    document.fonts?.ready.then(() => this.#fit()).catch(() => {});
  }

  disconnectedCallback() {
    this.#controller.abort();
    clearTimeout(this.#publishTimer);
  }

  #onInput() {
    const cleaned = cleanName(this.input.value);
    if (cleaned !== this.input.value) this.input.value = cleaned;
    this.#render(cleaned);
    this.#swing();
    setPetName(cleaned, 'hero');
    clearTimeout(this.#publishTimer);
    this.#publishTimer = window.setTimeout(() => {
      if (cleaned.trim().length >= 2) publishOnce('nalorio_pet_name_entered', { name_length: cleaned.trim().length, source: 'hero' });
    }, 900);
  }

  #render(value) {
    const label = (value.trim() || this.placeholder).toUpperCase();
    for (const t of this.texts) t.textContent = label;
    this.classList.toggle('is-placeholder', !value.trim());
    this.#fit();
  }

  /** Shrink the engraving so long names stay inside the tag face. */
  #fit() {
    const maxWidth = Number(this.dataset.maxWidth || 230);
    for (const t of this.texts) {
      t.style.fontSize = '';
      const base = parseFloat(getComputedStyle(t).fontSize) || 64;
      let width = 0;
      try {
        width = t.getComputedTextLength();
      } catch {
        width = 0;
      }
      if (width > maxWidth) t.style.fontSize = `${Math.max(22, (base * maxWidth) / width)}px`;
    }
  }

  #swing() {
    if (!this.swinger || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.swinger.classList.remove('is-swinging');
    void this.swinger.getBoundingClientRect(); // restart the animation
    this.swinger.classList.add('is-swinging');
  }
}

/* ---------- Bed size finder ---------- */

const BED_SIZES = [
  { max: 25, code: 'S', label: 'Small' },
  { max: 45, code: 'M', label: 'Medium' },
  { max: 80, code: 'L', label: 'Large' },
];

class BedFinder extends HTMLElement {
  #controller = new AbortController();
  #timer = 0;

  connectedCallback() {
    this.range = this.querySelector('[data-nl-weight]');
    this.weightOut = this.querySelector('[data-nl-weight-out]');
    this.sizeOut = this.querySelector('[data-nl-size-out]');
    this.noteOut = this.querySelector('[data-nl-size-note]');
    if (!(this.range instanceof HTMLInputElement)) return;
    this.range.addEventListener('input', () => this.#update(true), { signal: this.#controller.signal });
    this.#update(false);
  }

  disconnectedCallback() {
    this.#controller.abort();
    clearTimeout(this.#timer);
  }

  #update(fromUser) {
    const lb = Number(this.range.value);
    const size = BED_SIZES.find((s) => lb <= s.max);
    if (this.weightOut) this.weightOut.textContent = `${lb} lb`;
    this.range.setAttribute('aria-valuetext', `${lb} pounds`);
    if (size) {
      if (this.sizeOut) this.sizeOut.textContent = size.label;
      if (this.noteOut) this.noteOut.textContent = this.dataset[`note${size.code}`] || '';
    } else {
      if (this.sizeOut) this.sizeOut.textContent = this.dataset.overLabel || 'Ask us first';
      if (this.noteOut) this.noteOut.textContent = this.dataset.overNote || '';
    }
    this.dataset.size = size ? size.code : 'over';
    if (fromUser) {
      clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => publish('nalorio_bed_size_recommended', { weight_lb: lb, size: size ? size.code : 'over' }), 700);
    }
  }
}

/* ---------- Product personalization fields ---------- */

function wirePhotoFields() {
  for (const input of document.querySelectorAll('input[type="file"][data-nl-photo]')) {
    if (!(input instanceof HTMLInputElement) || input.dataset.nlWired) continue;
    input.dataset.nlWired = 'true';
    const preview = document.getElementById(input.dataset.nlPreview || '');
    const status = document.getElementById(input.dataset.nlStatus || '');
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      input.setCustomValidity('');
      if (status) status.textContent = '';
      if (preview instanceof HTMLImageElement) {
        if (preview.src) URL.revokeObjectURL(preview.src);
        preview.hidden = true;
      }
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        input.setCustomValidity(input.dataset.msgType || 'Choose an image file (JPG, PNG or HEIC).');
      } else if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
        input.setCustomValidity(input.dataset.msgSize || `That photo is over ${MAX_PHOTO_MB} MB. Choose a smaller one.`);
      }
      if (!input.validity.valid) {
        if (status) status.textContent = input.validationMessage;
        input.reportValidity();
        return;
      }
      if (preview instanceof HTMLImageElement) {
        preview.src = URL.createObjectURL(file);
        preview.hidden = false;
      }
      if (status) status.textContent = input.dataset.msgOk || 'Photo added.';
      publish('nalorio_photo_added', { size_mb: Math.round((file.size / 1048576) * 10) / 10, product: window.nalorio?.product?.handle });
    });
  }
}

function watchPersonalizedAddToCart() {
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.id.startsWith('BuyButtons-ProductForm-')) return;
      const fields = [...form.elements].filter((el) => el instanceof HTMLInputElement || el instanceof HTMLSelectElement)
        .filter((el) => el.name.startsWith('properties['));
      if (!fields.length) return;
      const nameField = fields.find((el) => el.name === 'properties[Pet name]');
      if (nameField instanceof HTMLInputElement && nameField.value.trim()) {
        const cleaned = cleanName(nameField.value).trim();
        if (cleaned && cleaned !== petStore.get()) setPetName(cleaned, 'product');
      }
      publish('nalorio_personalized_add_to_cart', {
        product: window.nalorio?.product?.handle,
        has_name: Boolean(nameField && nameField.value.trim()),
        has_photo: fields.some((el) => el instanceof HTMLInputElement && el.type === 'file' && el.files?.length),
      });
    },
    true
  );
}

function watchSignup() {
  for (const form of document.querySelectorAll('form[data-nl-signup]')) {
    form.addEventListener('submit', () => publish('nalorio_signup_submitted', { location: form.dataset.nlSignup || 'unknown' }));
  }
}

/* ---------- Boot ---------- */

if (!customElements.get('nl-name-tag')) customElements.define('nl-name-tag', NameTag);
if (!customElements.get('nl-bed-finder')) customElements.define('nl-bed-finder', BedFinder);

function boot() {
  const name = petStore.get();
  paintNames(name);
  prefillPropertyFields(name);
  wirePhotoFields();
  watchSignup();
}

watchPersonalizedAddToCart();
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

// Theme editor and section re-renders replace DOM; re-run the idempotent pieces.
document.addEventListener('shopify:section:load', boot);
